import { Avatar, NavigationBack, Typography } from "@/components";
import { MessageItem } from "@/components/ui";
import FloatingSearchV2 from "@/components/ui/floating-search/v2";
import InputText from "@/components/ui/message/input/InputText";
import useHemiPopup from "@/hooks/useHemiPopup";
import useProfile from "@/hooks/useProfile";
import { QUERY_KEYS } from "@/libraries/constant";
import { fromUnixToDateString } from "@/libraries/date";
import { genHemiUid, randUuid } from "@/libraries/helper";
import { applyDataChanges } from "@/libraries/transform";
import { getMessagesQueryOptions } from "@/queries";
import {
	getHemiMessageResponseOptions,
	getHemiMessagesOptions,
} from "@/queries/options/hemi";
import EmptyChat from "@/routes/dashboard/chat/-components/Empty";
import { type ChatGroupSearchSchema, MESSAGE_TAG } from "@/schemas";
import type {
	GetMessageDiscussionsResponse,
	MessageDiscussion,
	ResError,
} from "@/types";
import { zodResolver } from "@hookform/resolvers/zod";
import CachedIcon from "@mui/icons-material/Cached";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import {
	type InfiniteData,
	onlineManager,
	useMutation,
	useQueries,
	useQueryClient,
	useSuspenseInfiniteQuery,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useVirtualizer } from "@tanstack/react-virtual";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import flatMap from "lodash/flatMap";
import groupBy from "lodash/groupBy";
import isEmpty from "lodash/isEmpty";
import orderBy from "lodash/orderBy";
import uniqBy from "lodash/uniqBy";
import {
	Suspense,
	lazy,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";
import { useLongPress } from "use-long-press";
import { z } from "zod";

const ContextMenuMessage = lazy(() => import("./-components/ContextMenu"));

const HemiMessageFormSchema = z.object({
	text: z.string().optional(),
});
type HemiMessageFormSchema = z.infer<typeof HemiMessageFormSchema>;

const HemiPageSearchSchema = z.object({
	q: z.string().optional(),
});
type HemiPageSearchSchema = z.infer<typeof HemiPageSearchSchema>;

export const Route = createFileRoute("/dashboard/chat/single/hemi/")({
	loader: async ({ context }) => {
		if (onlineManager.isOnline() && !context.isRestoring) {
			const hemiQuery = await context.queryClient.ensureQueryData(
				getHemiMessagesOptions(),
			);
			const messagesQuery = await context.queryClient.ensureInfiniteQueryData(
				getMessagesQueryOptions({
					channel_name: hemiQuery.conversation.channel_name,
					tags: MESSAGE_TAG.AI,
				}),
			);
			return [hemiQuery, messagesQuery];
		}
		const hemiData = context.queryClient.getQueryData([
			QUERY_KEYS.GET_HEMI_MESSAGES,
		]) as any;
		const messagesData = context.queryClient.getQueryData([
			QUERY_KEYS.GET_MESSAGES,
			{
				channel_name: hemiData.conversation.channel_name,
				tags: MESSAGE_TAG.AI,
			},
		]);
		return [hemiData, messagesData];
	},
	validateSearch: zodSearchValidator(HemiPageSearchSchema),
	component: RouteComponent,
});

function RouteComponent() {
	const queryClient = useQueryClient();
	const navigate = Route.useNavigate();
	const searchQ = Route.useSearch({
		select: (state) => String(state.q ?? ""),
	});
	const scrollRef = useRef<HTMLDivElement>(null);
	const { profile } = useProfile();
	const create = useMutation<
		unknown,
		ResError,
		Record<"uid" | "text" | "channel_name", string>
	>({
		mutationKey: [QUERY_KEYS.MUTATE_CREATE_HEMI_MESSAGE],
	});

	const [contextMessage, setContextMessage] =
		useState<MessageDiscussion | null>(null);

	const [cursorPosition, setCursorPosition] = useState<Record<
		"x" | "y",
		number
	> | null>(null);

	const hemiQuery = useSuspenseQuery(getHemiMessagesOptions());

	const formMethod = useForm({
		mode: "all",
		resolver: zodResolver(HemiMessageFormSchema),
	});
	const { reset, handleSubmit } = formMethod;

	const queryMessages = useSuspenseInfiniteQuery(
		getMessagesQueryOptions({
			channel_name: hemiQuery.data.conversation.channel_name,
			tags: MESSAGE_TAG.AI,
		}),
	);

	const resMessages = useMemo(() => {
		const messageItems = queryMessages.data.pages
			.flatMap((f) => f.messages)
			.map((m) => ({
				...m,
				tags: !isEmpty(m.hemi_responses) ? MESSAGE_TAG.AI : null,
				...(!isEmpty(m.hemi_responses)
					? {
							system_message: false,
							user: {
								id: hemiQuery.data.conversation.id,
								fullname: "HEMI AI",
								status: "available",
							},
							reads: [profile?.id],
							_flag: "read",
						}
					: {}),
				...(m.user?.id === profile?.id
					? {
							_flag: "read",
						}
					: {}),
			}));
		const orderedMessages = orderBy(
			messageItems,
			[(m) => m.updated_at, (m) => (!m.hemi_responses?.length ? 0 : 1)],
			["desc", "desc"],
		);
		const groupedMessages = groupBy(orderedMessages, (m) =>
			fromUnixToDateString(m.updated_at),
		);
		const flatMessages = flatMap(
			groupedMessages,
			(messages, date) =>
				[
					...messages,
					{
						id: randUuid(10),
						text: date,
						system_message: true,
						tags: "date",
						channel_name: hemiQuery.data.conversation.channel_name ?? "",
						id_db: 0,
						created_at: messages[0].created_at,
						updated_at: messages[0].updated_at,
						document_count: 0,
						document_size: "0.0 Kb",
						sent: false,
						delivered: false,
						strike: false,
						is_deleted: false,
						reply_to: null,
						invitation_status: null,
						latest_reply_date: null,
						reply_count: 0,
						reads: [],
						mention_name: [],
						user: null,
						parent: null,
						replies: [],
						documents: [],
						mentions: [],
						preview: null,
					},
				] as MessageDiscussion[],
		);
		return uniqBy(flatMessages, "id");
	}, [queryMessages.data, hemiQuery.data, profile?.id]);
	const messageCount = resMessages.length;

	const messageVirtualizer = useVirtualizer({
		enabled: messageCount > 0,
		count: messageCount,
		overscan: 5,
		getScrollElement: () => scrollRef.current,
		measureElement: (el) => el.getBoundingClientRect().height,
		estimateSize: () => 50,
	});
	const messageVirtualizerItems = messageVirtualizer.getVirtualItems();

	const onSubmit: SubmitHandler<HemiMessageFormSchema> = (data, e) => {
		if (!data.text) return;
		e?.stopPropagation();
		e?.preventDefault();
		create.mutate({
			uid: genHemiUid(),
			text: data.text,
			channel_name: hemiQuery.data.conversation.channel_name,
		});
		reset();
	};

	const onChangeSearch = (val: string) =>
		navigate({
			search: (oldSearch: ChatGroupSearchSchema) => ({
				...oldSearch,
				q: val || undefined,
			}),
			replace: true,
		});

	const pendingIds = useMemo(
		() =>
			resMessages
				.filter((f) => f.hemi_responses?.[0]?.status === "pending")
				.map((m) => m.id),
		[resMessages],
	);

	const pendingQueries = useQueries({
		queries: pendingIds.length
			? pendingIds.map((uid) =>
					getHemiMessageResponseOptions(uid, {
						refetchInterval: 3000, // 3 seconds
						refetchIntervalInBackground: true,
					}),
				)
			: [],
	});
	const dataPendingQueries = pendingQueries.filter(
		(query) => query.data?.message,
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		const handleScroll = (e: WheelEvent) => {
			e.preventDefault();
			const currentTarget = e.currentTarget as HTMLElement;

			if (currentTarget) {
				currentTarget.scrollTop -= e.deltaY;
			}
		};
		scrollRef.current?.addEventListener("wheel", handleScroll, {
			passive: false,
		});
		return () => {
			scrollRef.current?.removeEventListener("wheel", handleScroll);
		};
	}, [queryMessages.status]);

	useEffect(() => {
		const [lastItem] = [...messageVirtualizerItems].reverse();

		if (!lastItem) {
			return;
		}

		if (
			lastItem.index >= resMessages.length - 1 &&
			queryMessages.hasNextPage &&
			!queryMessages.isFetchingNextPage
		) {
			queryMessages.fetchNextPage();
		}
	}, [
		resMessages.length,
		queryMessages.hasNextPage,
		queryMessages.fetchNextPage,
		queryMessages.isFetchingNextPage,
		messageVirtualizerItems,
	]);

	useEffect(() => {
		if (dataPendingQueries.length) {
			for (const dataPendingQuery of dataPendingQueries) {
				const pendingMessageStatus =
					dataPendingQuery.data?.message?.hemi_responses?.[0]?.status;
				if (!pendingMessageStatus || pendingMessageStatus === "pending")
					continue;
				if (pendingMessageStatus === "complete") {
					queryClient.setQueryData<InfiniteData<GetMessageDiscussionsResponse>>(
						[
							QUERY_KEYS.GET_MESSAGES,
							{
								channel_name: hemiQuery.data.conversation.channel_name,
								tags: MESSAGE_TAG.AI,
							},
						],
						(oldData) =>
							!oldData
								? undefined
								: {
										...oldData,
										pages:
											oldData.pages.map((p) => ({
												...p,
												messages: applyDataChanges(
													p.messages,
													[dataPendingQuery.data?.message as any],
													[],
												),
											})) ?? [],
										pageParams: oldData.pageParams,
									},
					);
				}
			}
		}
	}, [dataPendingQueries, queryClient, hemiQuery]);

	const onOpenContextMenu = useCallback(
		(e: React.MouseEvent<HTMLDivElement>, message: MessageDiscussion) => {
			e.preventDefault();
			e.stopPropagation();
			setContextMessage(message);
			return setCursorPosition({ x: e.clientX, y: e.clientY });
		},
		[],
	);

	const onCloseContextMenu = useCallback(() => {
		setCursorPosition(null);
		return setContextMessage(null);
	}, []);

	const onLongPressMessage = useLongPress<HTMLDivElement, MessageDiscussion>(
		(e, meta) =>
			meta.context &&
			onOpenContextMenu(e as React.MouseEvent<HTMLDivElement>, meta.context),
	);

	useHemiPopup();

	return (
		<>
			<AppBar
				position="relative"
				color="transparent"
				className="!bg-gradient-chat [&_*_]:text-white"
			>
				<Toolbar>
					<NavigationBack />
					<div className="w-full relative flex gap-1 justify-between py-3">
						<FloatingSearchV2
							name="search"
							value={searchQ}
							onClear={() => onChangeSearch("")}
							onChange={(e) => onChangeSearch(e.target.value)}
							className="!rounded-xl"
							inputClassName="!rounded-xl"
						>
							<div className="flex items-center">
								<Avatar
									src="/images/hemi.png"
									alt="HEMI AI"
									sx={{ mr: 1 }}
									className="!bg-brand !p-1"
								/>
								<Typography fontWeight={600} fontSize={14}>
									HEMI AI
								</Typography>
							</div>
						</FloatingSearchV2>
					</div>
				</Toolbar>
			</AppBar>
			<div
				ref={scrollRef}
				className="relative flex-1 w-full overflow-auto"
				style={{
					transform: messageCount ? "scaleY(-1)" : undefined,
				}}
			>
				{messageCount && profile ? (
					<div
						className="relative w-full"
						style={{
							height: messageVirtualizer.getTotalSize(),
						}}
					>
						{messageVirtualizerItems.map((item) => {
							const message = resMessages[item.index];
							if (pendingIds.includes(message.id)) {
								<div
									key={item.key}
									ref={messageVirtualizer.measureElement}
									data-index={item.index}
									className="absolute top-0 left-0 w-full"
									style={{
										transform: `translateY(${item.start}px) scaleY(-1)`,
									}}
								>
									<CachedIcon className="animate-spin w-6 h-6" />
								</div>;
							}
							return (
								<div
									key={item.key}
									ref={messageVirtualizer.measureElement}
									data-index={item.index}
									className="absolute top-0 left-0 w-full"
									style={{
										transform: `translateY(${item.start}px) scaleY(-1)`,
									}}
								>
									<MessageItem
										key={message.id}
										profileId={profile?.id}
										message={message}
										className="container !py-2"
										chatModule="chats"
										onContextMenu={(e) => onOpenContextMenu(e, message)}
										{...onLongPressMessage(message)}
									/>
								</div>
							);
						})}
					</div>
				) : (
					<EmptyChat />
				)}
			</div>
			<FormProvider {...formMethod}>
				<form
					onSubmit={handleSubmit(onSubmit)}
					className="relative px-3 py-2 bg-white"
				>
					<InputText type="submit" disabled={dataPendingQueries.length > 0} />
				</form>
			</FormProvider>
			{contextMessage ? (
				<Suspense>
					<ContextMenuMessage
						message={contextMessage}
						position={cursorPosition}
						onClose={onCloseContextMenu}
					/>
				</Suspense>
			) : null}
		</>
	);
}
